import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getRisks, createRisk, updateRisk, runRiskAIAnalysis, downloadRiskRegisterPDF } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Plus, Search, AlertTriangle, Brain, Download, Eye, Edit,
  ChevronLeft, Sparkles, MessageSquare, Plane
} from 'lucide-react';
import CollaborationPanel from '@/components/CollaborationPanel';
import AssetSelector, { AssetDisplay } from '@/components/AssetSelector';
import HelpTooltip from '@/components/help/HelpTooltip';
import AIFieldSuggestions from '@/components/AIFieldSuggestions';
import PolishButton from '@/components/PolishButton';

const SEVERITIES = ['catastrophic', 'hazardous', 'major', 'minor', 'negligible'];
const LIKELIHOODS = ['frequent', 'occasional', 'remote', 'improbable', 'extremely_improbable'];
const RISK_LEVELS = ['critical', 'high', 'medium', 'low', 'acceptable'];
const CATEGORIES = ['operational', 'technical', 'organizational', 'human_factors', 'environmental'];

const RISK_COLORS = {
  critical: 'risk-critical',
  high: 'risk-high',
  medium: 'risk-medium',
  low: 'risk-low',
  acceptable: 'risk-acceptable',
};

// Risk Matrix Component
const RiskMatrix = () => {
  const severities = ['Catastrophic', 'Hazardous', 'Major', 'Minor', 'Negligible'];
  const likelihoods = ['Frequent', 'Occasional', 'Remote', 'Improbable', 'Extremely\nImprobable'];
  
  const getMatrixColor = (sev, lik) => {
    const score = (5 - sev) * (5 - lik);
    if (score >= 16) return 'bg-red-600';
    if (score >= 9) return 'bg-orange-500';
    if (score >= 4) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="p-2 text-slate-500 text-left">Severity / Likelihood</th>
            {likelihoods.map((l, i) => (
              <th key={i} className="p-2 text-slate-400 text-center whitespace-pre-line w-20">{l}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {severities.map((sev, sevIdx) => (
            <tr key={sevIdx}>
              <td className="p-2 text-slate-300 font-medium">{sev}</td>
              {likelihoods.map((_, likIdx) => (
                <td key={likIdx} className="p-1">
                  <div className={`w-full h-8 rounded ${getMatrixColor(sevIdx, likIdx)} opacity-80`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-4 mt-3 justify-center text-xs">
        <div className="flex items-center gap-1"><div className="w-4 h-4 bg-red-600 rounded" /><span className="text-slate-400">Critical</span></div>
        <div className="flex items-center gap-1"><div className="w-4 h-4 bg-orange-500 rounded" /><span className="text-slate-400">High</span></div>
        <div className="flex items-center gap-1"><div className="w-4 h-4 bg-yellow-500 rounded" /><span className="text-slate-400">Medium</span></div>
        <div className="flex items-center gap-1"><div className="w-4 h-4 bg-emerald-500 rounded" /><span className="text-slate-400">Low</span></div>
      </div>
    </div>
  );
};

export default function RisksPage({ initialMode }) {
  const { t } = useTranslation();
  const { api, token, user } = useAuth();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(initialMode || 'list');
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [filters, setFilters] = useState({ risk_level: '', category: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showCollaboration, setShowCollaboration] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'operational',
    source: 'proactive',
    severity: 'major',
    likelihood: 'remote',
    // Asset linking fields
    aircraft_id: null,
    aircraft_registration: null,
    engine_id: null,
    component_id: null,
  });

  useEffect(() => {
    if (mode === 'list') {
      fetchRisks();
    }
  }, [mode, filters]);

  const fetchRisks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.risk_level) params.risk_level = filters.risk_level;
      if (filters.category) params.category = filters.category;
      
      const response = await getRisks(api, params);
      setRisks(response.data);
    } catch (error) {
      console.error('Failed to fetch risks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createRisk(api, formData);
      setMode('list');
      resetForm();
    } catch (error) {
      console.error('Failed to create risk:', error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateRisk(api, selectedRisk.id, formData);
      setMode('list');
      resetForm();
    } catch (error) {
      console.error('Failed to update risk:', error);
    }
  };

  const handleRunAI = async (riskId) => {
    setAiLoading(true);
    try {
      const response = await runRiskAIAnalysis(api, riskId);
      if (selectedRisk) {
        setSelectedRisk({ ...selectedRisk, ai_analysis: response.data });
      }
      fetchRisks();
    } catch (error) {
      console.error('Failed to run AI analysis:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await downloadRiskRegisterPDF(token);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'risk_register.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'operational',
      source: 'proactive',
      severity: 'major',
      likelihood: 'remote',
      aircraft_id: null,
      aircraft_registration: null,
      engine_id: null,
      component_id: null,
    });
    setSelectedRisk(null);
  };

  const filteredRisks = risks.filter(risk => 
    risk.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    risk.risk_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // List View
  if (mode === 'list') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="risks-title">
              {t('risks.title')}
            </h1>
            <p className="text-slate-400 mt-1">Manage and monitor safety risks</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              className="border-slate-700 text-slate-300"
              data-testid="download-risk-register-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button
              onClick={() => setMode('create')}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="new-risk-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('risks.newRisk')}
            </Button>
          </div>
        </div>

        {/* Risk Matrix Card */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              {t('risks.riskMatrix')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RiskMatrix />
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search risks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-950 border-slate-800"
                    data-testid="risk-search"
                  />
                </div>
              </div>
              <Select value={filters.risk_level || "all"} onValueChange={(v) => setFilters({ ...filters, risk_level: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[150px] bg-slate-950 border-slate-800" data-testid="filter-risk-level">
                  <SelectValue placeholder="Risk Level" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all">All Levels</SelectItem>
                  {RISK_LEVELS.map(l => (
                    <SelectItem key={l} value={l}>{t(`risks.levels.${l}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[150px] bg-slate-950 border-slate-800" data-testid="filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Risks Table */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : filteredRisks.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t('common.noData')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">{t('risks.riskNumber')}</TableHead>
                    <TableHead className="text-slate-400">Title</TableHead>
                    <TableHead className="text-slate-400">Category</TableHead>
                    <TableHead className="text-slate-400">{t('incidents.severity')}</TableHead>
                    <TableHead className="text-slate-400">{t('incidents.likelihood')}</TableHead>
                    <TableHead className="text-slate-400">{t('incidents.riskLevel')}</TableHead>
                    <TableHead className="text-slate-400">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRisks.map((risk) => (
                    <TableRow key={risk.id} className="border-slate-800 hover:bg-slate-800/50" data-testid={`risk-row-${risk.id}`}>
                      <TableCell className="font-mono text-sky-400">{risk.risk_number}</TableCell>
                      <TableCell className="text-white max-w-[200px] truncate">{risk.title}</TableCell>
                      <TableCell className="text-slate-300 capitalize">{risk.category?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-slate-300 capitalize">{risk.severity}</TableCell>
                      <TableCell className="text-slate-300 capitalize">{risk.likelihood?.replace('_', ' ')}</TableCell>
                      <TableCell>
                        <Badge className={`${RISK_COLORS[risk.risk_level]} px-2.5 py-0.5 rounded-sm text-xs font-medium`}>
                          {t(`risks.levels.${risk.risk_level}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedRisk(risk); setMode('view'); }}
                            className="text-slate-400 hover:text-white"
                            data-testid={`view-risk-${risk.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { 
                              setSelectedRisk(risk); 
                              setFormData({
                                mitigation_plan: risk.mitigation_plan || '',
                                severity: risk.severity,
                                likelihood: risk.likelihood
                              });
                              setMode('edit'); 
                            }}
                            className="text-slate-400 hover:text-white"
                            data-testid={`edit-risk-${risk.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Create View
  if (mode === 'create') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => { setMode('list'); resetForm(); }}
            className="text-slate-400 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white font-barlow">
            {t('risks.newRisk')}
          </h1>
        </div>

        <Card className="bg-slate-900/60 border-slate-800 max-w-3xl">
          <CardContent className="p-6">
            <form onSubmit={handleCreate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-slate-300">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="Risk title"
                    data-testid="risk-title-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">{t('incidents.description')} *</Label>
                    <PolishButton
                      text={formData.description}
                      kind="risk"
                      token={token}
                      onApply={(polished) => setFormData((prev) => ({ ...prev, description: polished }))}
                    />
                  </div>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={4}
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="Detailed risk description..."
                    data-testid="risk-description-input"
                  />
                  <AIFieldSuggestions
                    kind="risk"
                    title={formData.title}
                    description={formData.description}
                    token={token}
                    onApply={(patch) => setFormData((prev) => ({
                      ...prev,
                      ...(patch.severity && { severity: patch.severity }),
                      ...(patch.likelihood && { likelihood: patch.likelihood }),
                      ...(patch.risk_category && { category: patch.risk_category }),
                    }))}
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="risk-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {CATEGORIES.map(c => (
                        <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300">Source *</Label>
                  <Select 
                    value={formData.source} 
                    onValueChange={(v) => setFormData({ ...formData, source: v })}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="risk-source-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="proactive">Proactive</SelectItem>
                      <SelectItem value="incident">Incident</SelectItem>
                      <SelectItem value="hazard">Hazard Report</SelectItem>
                      <SelectItem value="audit">Audit Finding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300 inline-flex items-center">
                    {t('incidents.severity')} *
                    <HelpTooltip
                      label="Severity (ICAO Annex 19)"
                      body={"How bad the outcome could be if the hazard happens, on the 5-point ICAO Annex 19 / GACAR scale:\n\n• Catastrophic — hull loss / multiple fatalities\n• Hazardous — large reduction in safety margins; serious injuries\n• Major — significant reduction; injuries; equipment damage\n• Minor — nuisance; small operational limitations\n• Negligible — little consequence\n\nUse worst credible outcome, not the actual outcome this time."}
                      articleId="task-risk-scoring"
                      example="major"
                      onApplyExample={(v) => setFormData({ ...formData, severity: v })}
                    />
                  </Label>
                  <Select 
                    value={formData.severity} 
                    onValueChange={(v) => setFormData({ ...formData, severity: v })}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="risk-severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {SEVERITIES.map(s => (
                        <SelectItem key={s} value={s}>{t(`incidents.severities.${s}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300 inline-flex items-center">
                    {t('incidents.likelihood')} *
                    <HelpTooltip
                      label="Likelihood (ICAO Annex 19)"
                      body={"How often the hazard is expected to happen:\n\n• Frequent — likely many times (weekly / monthly)\n• Occasional — likely sometimes (a few times a year)\n• Remote — unlikely but possible (once every few years)\n• Improbable — very unlikely (may never happen)\n• Extremely Improbable — should virtually never happen\n\nBase this on your fleet size and operation, not gut feel."}
                      articleId="task-risk-scoring"
                      example="occasional"
                      onApplyExample={(v) => setFormData({ ...formData, likelihood: v })}
                    />
                  </Label>
                  <Select 
                    value={formData.likelihood} 
                    onValueChange={(v) => setFormData({ ...formData, likelihood: v })}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="risk-likelihood-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {LIKELIHOODS.map(l => (
                        <SelectItem key={l} value={l}>{l.replace('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Asset Selector - Link to Aircraft/Engine/Component */}
                <div className="md:col-span-2">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <AssetSelector
                        value={{
                          aircraft_id: formData.aircraft_id,
                          aircraft_registration: formData.aircraft_registration,
                          engine_id: formData.engine_id,
                          component_id: formData.component_id,
                        }}
                        onChange={(assetData) => {
                          setFormData({
                            ...formData,
                            aircraft_id: assetData.aircraft_id,
                            aircraft_registration: assetData.aircraft_registration,
                            engine_id: assetData.engine_id,
                            component_id: assetData.component_id,
                          });
                        }}
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-risk-btn">
                  {t('common.create')}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setMode('list'); resetForm(); }}
                  className="border-slate-700 text-slate-300"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // View/Edit Mode
  if (mode === 'view' || mode === 'edit') {
    const risk = selectedRisk;
    
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => { setMode('list'); resetForm(); }}
              className="text-slate-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white font-barlow" data-testid="risk-detail-title">
                {risk.risk_number}
              </h1>
              <p className="text-slate-400">{risk.title}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleRunAI(risk.id)}
              disabled={aiLoading}
              className="border-violet-600/50 text-violet-400 hover:bg-violet-600/20"
              data-testid="run-risk-ai-btn"
            >
              {aiLoading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner w-4 h-4" />
                  Analyzing...
                </span>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  AI Analysis
                </>
              )}
            </Button>
            {mode === 'view' && (
              <Button onClick={() => setMode('edit')} className="bg-sky-600 hover:bg-sky-700">
                <Edit className="w-4 h-4 mr-2" />
                {t('common.edit')}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowCollaboration(!showCollaboration)}
              className={`border-slate-700 ${showCollaboration ? 'bg-cyan-600/20 border-cyan-600/50 text-cyan-400' : 'text-slate-300'}`}
              data-testid="toggle-collaboration-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Collaborate
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Risk Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.riskLevel')}</Label>
                    <Badge className={`${RISK_COLORS[risk.risk_level]} px-3 py-1 mt-1 text-sm`}>
                      {t(`risks.levels.${risk.risk_level}`)}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">Category</Label>
                    <p className="text-white mt-1 capitalize">{risk.category?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.severity')}</Label>
                    <p className="text-white mt-1 capitalize">{risk.severity}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">{t('incidents.likelihood')}</Label>
                    <p className="text-white mt-1 capitalize">{risk.likelihood?.replace('_', ' ')}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">{t('incidents.description')}</Label>
                  <p className="text-slate-300 mt-1 whitespace-pre-wrap">{risk.description}</p>
                </div>

                {/* Linked Assets */}
                {(risk.aircraft_id || risk.engine_id || risk.component_id) && (
                  <div className="pt-4 border-t border-slate-800">
                    <Label className="text-slate-500 text-sm flex items-center gap-2">
                      <Plane className="w-4 h-4" />
                      Linked Assets
                    </Label>
                    <div className="mt-2">
                      <AssetDisplay
                        aircraftId={risk.aircraft_id}
                        aircraftRegistration={risk.aircraft_registration}
                        engineId={risk.engine_id}
                        engineSerial={risk.engine_serial}
                        componentId={risk.component_id}
                        componentPartNumber={risk.component_part_number}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">{t('risks.mitigation')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {mode === 'edit' ? (
                  <>
                    <div>
                      <Label className="text-slate-500 text-sm">Mitigation Plan</Label>
                      <Textarea
                        value={formData.mitigation_plan || ''}
                        onChange={(e) => setFormData({ ...formData, mitigation_plan: e.target.value })}
                        rows={4}
                        className="bg-slate-950 border-slate-800 mt-1"
                        placeholder="Describe mitigation measures..."
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button onClick={handleUpdate} className="bg-sky-600 hover:bg-sky-700">
                        {t('common.save')}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setMode('view')}
                        className="border-slate-700 text-slate-300"
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div>
                    <Label className="text-slate-500 text-sm">Mitigation Plan</Label>
                    <p className="text-slate-300 mt-1 whitespace-pre-wrap">{risk.mitigation_plan || 'No mitigation plan defined'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* AI Analysis Sidebar */}
          <div>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-violet-400" />
                  AI Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                {risk.ai_analysis && !risk.ai_analysis.error ? (
                  <div className="space-y-4 text-sm">
                    {risk.ai_analysis.risk_assessment && (
                      <div>
                        <Label className="text-slate-500 text-sm">Assessment</Label>
                        <p className="text-slate-300 mt-1">{risk.ai_analysis.risk_assessment}</p>
                      </div>
                    )}
                    {risk.ai_analysis.mitigation_strategies && (
                      <div>
                        <Label className="text-slate-500 text-sm">Suggested Mitigations</Label>
                        <ul className="mt-1 space-y-1">
                          {risk.ai_analysis.mitigation_strategies.map((m, i) => (
                            <li key={i} className="text-emerald-400 flex items-start gap-2">
                              <Sparkles className="w-3 h-3 mt-1 flex-shrink-0" />
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {risk.ai_analysis.monitoring_indicators && (
                      <div>
                        <Label className="text-slate-500 text-sm">Monitoring Indicators</Label>
                        <ul className="mt-1 space-y-1">
                          {risk.ai_analysis.monitoring_indicators.map((m, i) => (
                            <li key={i} className="text-slate-400 flex items-start gap-2">
                              <AlertTriangle className="w-3 h-3 mt-1 flex-shrink-0 text-orange-400" />
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <Brain className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No AI analysis available</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRunAI(risk.id)}
                      disabled={aiLoading}
                      className="mt-3 border-violet-600/50 text-violet-400"
                    >
                      Run Analysis
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Collaboration Panel */}
        {showCollaboration && (
          <CollaborationPanel
            entityType="risk"
            entityId={risk.id}
            entityTitle={risk.title}
            apiClient={api}
            currentUser={user}
            token={token}
            isOpen={showCollaboration}
            onClose={() => setShowCollaboration(false)}
          />
        )}
      </div>
    );
  }

  return null;
}
